import { faQuoteLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Chip, Dialog, DialogContent, Divider, Grid, IconButton, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import DialogTitle from "../../../../../../../../../../containers/DialogTitle/DialogTitle";
import { Page } from "../../../../../../../../../../models/api/page";
import { AvailableLanguageRef } from "../../../../../../../../../../models/available-language";
import { TravelExpenseSuggestedNoteRef } from "../../../../../../../../../../models/travel-expense-suggested-note";
import { TravelExpenseOrderedSuggestedNotesFilter } from "../../../../../../../../../../models/travel-expense-suggested-note/travel-expense-ordered-suggested-notes/travel-expense-ordered-suggested-notes-filter";
import { TravelExpenseOrderedSuggestedNotes } from "../../../../../../../../../../services/travel-expense-suggested-note/travel-expenses-ordered-suggested-notes-api.service";

interface Props {
  language?: AvailableLanguageRef | null;
  handleChangeSuggestedNote?: (note: TravelExpenseSuggestedNoteRef) => void;
  latestSuggestedNote?: TravelExpenseSuggestedNoteRef;
  travelExpenseTypeId?: string;
}

interface State {
  suggestedNotes: TravelExpenseSuggestedNoteRef[];
  others: TravelExpenseSuggestedNoteRef[];
  loading: boolean;
  error: string;
  page: Page;
  open: boolean;
}

const SuggestedNotes = ({ language, handleChangeSuggestedNote, latestSuggestedNote, travelExpenseTypeId }: Props) => {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    suggestedNotes: [],
    others: [],
    loading: false,
    error: "",
    page: {
      pageSize: 100,
      pageNumber: 0,
    },
    open: false,
  });

  const loadSuggestedNotes = useCallback(async () => {
    try {
      const response = await new TravelExpenseOrderedSuggestedNotes().loadItems(
        {
          suggestedAfterId: latestSuggestedNote?.id,
          languageId: language?.id,
          travelExpenseTypeId,
        } as TravelExpenseOrderedSuggestedNotesFilter,
        undefined,
        undefined,
        state.page,
        undefined,
        60,
        undefined
      );

      if (!response.error) {
        setState((state) => ({
          ...state,
          error: "",
          suggestedNotes: response.items[0].suggestedNotes,
          others: response.items[0].others,
        }));
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loadingSuggestedNotes: false }));
    }
  }, [latestSuggestedNote, state.page, language, travelExpenseTypeId]);

  useEffect(() => {
    if (state.open) loadSuggestedNotes();
  }, [state.page, latestSuggestedNote, state.open, language, travelExpenseTypeId]);

  return (
    <>
      <IconButton
        onClick={() => setState((state) => ({ ...state, open: true }))}
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        <FontAwesomeIcon icon={faQuoteLeft} />
      </IconButton>
      <Dialog
        open={state.open}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          title="Suggested notes"
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />
        <DialogContent>
          <Grid style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Divider>
              <Chip
                label="Suggested notes"
                style={{ color: theme.palette.secondary.light }}
              />
            </Divider>
            {state.suggestedNotes.length > 0 ? (
              state.suggestedNotes.map((suggestedNote, idx) => (
                <Button
                  variant="outlined"
                  key={idx}
                  onClick={() => {
                    setState((state) => ({ ...state, open: false }));
                    handleChangeSuggestedNote && handleChangeSuggestedNote(suggestedNote);
                  }}
                  style={{ textTransform: "none" }}
                >
                  {suggestedNote.note}
                </Button>
              ))
            ) : (
              <Typography
                variant="body2"
                style={{ color: theme.palette.secondary.light }}
              >
                No suggested notes
              </Typography>
            )}

            {state.others.length > 0 && (
              <>
                <Divider>
                  <Chip
                    label="Others"
                    style={{ color: theme.palette.secondary.light }}
                  />
                </Divider>
                {state.others.map((suggestedNote, idx) => (
                  <Button
                    variant="outlined"
                    key={idx}
                    onClick={() => {
                      setState((state) => ({ ...state, open: false }));
                      handleChangeSuggestedNote && handleChangeSuggestedNote(suggestedNote);
                    }}
                    style={{ textTransform: "none", justifyContent: "flex-start" }}
                  >
                    {suggestedNote.note}
                  </Button>
                ))}
              </>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SuggestedNotes;
